import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import styled from 'styled-components';
import { FaSearch, FaTimes } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import { v4 as uuidv4 } from 'uuid';
import { useMarkers } from '../../hooks/useMarkers';
import { MarkerData } from '../../types/marker';
import { UnifiedMarkerModal } from '../modal/UnifiedMarkerModal';
import useWindowSize from '../../hooks/useWindowSize';
import { useUserProfile } from '../../hooks/useUserProfile';

// Conteneur principal qui est toujours présent
const SearchContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 370px; /* Positionné à droite du bouton de la sidebar (310px + 40px + 20px) */
  z-index: 1002; /* Augmenter le z-index pour qu'il soit au-dessus de la sidebar */
  display: flex;
  gap: 10px;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2));
  
  @media (max-width: 768px) {
    left: 55px; /* Rapprocher du bouton de repli */
    width: auto;
    display: flex !important;
  }
`;

// Conteneur de la barre de recherche qui peut être caché/affiché
const SearchWrapperContainer = styled.div<{ isVisible: boolean }>`
  display: flex;
  width: 400px;
  transition: all 0.3s ease-in-out;
  
  @media (max-width: 1024px) {
    width: ${({ isVisible }) => (isVisible ? '300px' : '0')};
    opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
    overflow: hidden;
    margin-right: ${({ isVisible }) => (isVisible ? '10px' : '0')};
  }
  
  @media (max-width: 768px) {
    width: ${({ isVisible }) => (isVisible ? '250px' : '0')};
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0; // Pour éviter le débordement
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px;
  padding-right: 40px;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
`;

const ToggleButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #2c3e50; /* Couleur de la sidebar */
  color: white;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s, transform 0.2s;
  margin-left: 6px; /* Réduire l'écartement entre les boutons */
  
  &:hover {
    background-color: #34495e; /* Version plus claire de la couleur de la sidebar */
    transform: scale(1.05);
  }
  
  svg {
    font-size: 18px;
  }
  
  &:focus {
    outline: none;
  }
  
  @media (min-width: 1025px) {
    display: none; /* Cacher le bouton sur desktop */
  }
`;

const SuggestionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1010;
  border: 1px solid #e0e0e0;
`;

const SuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
`;

interface SearchBarProps {
  map: React.MutableRefObject<mapboxgl.Map | null>;
  onAddCategory?: () => void;
  sidebarCollapsed: boolean;
}

const SearchBar: React.FC<SearchBarProps> = memo(({ map, onAddCategory: _onAddCategory, sidebarCollapsed }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<MarkerData | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(true);
  const searchTimeout = useRef<NodeJS.Timeout>();
  const { addMarker } = useMarkers(map);
  const { isMobile, isTablet } = useWindowSize();
  const { userProfile } = useUserProfile();
  const _isOwner = userProfile?.is_owner || false;
  
  // Définir la visibilité initiale en fonction de la taille de l'écran
  useEffect(() => {
    setIsSearchVisible(!(isMobile || isTablet));
  }, [isMobile, isTablet]);

  const searchMapboxAPI = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        const accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
        // Amélioration des paramètres pour de meilleures suggestions
        const apiUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${accessToken}&country=fr&language=fr&limit=5&types=address,place,locality,neighborhood,postcode&autocomplete=true`;

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }
        
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          console.log('Suggestions reçues:', data.features.length);
          setSuggestions(data.features);
        } else {
          console.log('Aucune suggestion trouvée');
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche d\'adresses:', error);
        setSuggestions([]);
      }
    }, 300);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    searchMapboxAPI(value);
  }, [searchMapboxAPI]);

  const handleSuggestionClick = useCallback((suggestion: any) => {
    if (!map.current) return;

    const [lng, lat] = suggestion.center;
    map.current.flyTo({
      center: [lng, lat],
      zoom: 15
    });

    setSelectedLocation({
      id: uuidv4(),
      coordinates: [lng, lat],
      address: suggestion.place_name,
      categoryId: null
    });

    setSuggestions([]);
    setSearchTerm('');
  }, [map]);

  const handleCloseModal = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  const handleSaveMarker = useCallback(async (data: MarkerData): Promise<MarkerData> => {
    if (selectedLocation) {
      const createdMarker = await addMarker(selectedLocation.coordinates, data);
      handleCloseModal();
      return createdMarker;
    }
    throw new Error('Emplacement non sélectionné');
  }, [selectedLocation, addMarker, handleCloseModal]);

  const toggleSearchVisibility = useCallback(() => {
    setIsSearchVisible(prev => !prev);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  return (
    <>
      <SearchContainer>
        {/* Barre de recherche - visible uniquement quand la sidebar est fermée en mode mobile */}
        {(sidebarCollapsed || !isMobile) && (
          <SearchWrapperContainer isVisible={isSearchVisible}>
            <SearchWrapper>
              <SearchInput
                type="text"
                placeholder="Rechercher une adresse..."
                value={searchTerm}
                onChange={handleInputChange}
              />
              <SearchIcon>
                <FaSearch />
              </SearchIcon>
              {suggestions.length > 0 && (
                <SuggestionsList>
                  {suggestions.map((suggestion) => {
                    // Extraire le type de lieu pour l'afficher
                    const placeType = suggestion.place_type && suggestion.place_type[0] ? 
                      suggestion.place_type[0].charAt(0).toUpperCase() + suggestion.place_type[0].slice(1) : 
                      'Lieu';
                    
                    return (
                      <SuggestionItem
                        key={suggestion.id}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <div style={{ fontWeight: 'bold' }}>{suggestion.text}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{suggestion.place_name}</div>
                        <div style={{ fontSize: '0.75em', color: '#999', marginTop: '2px' }}>{placeType}</div>
                      </SuggestionItem>
                    );
                  })}
                </SuggestionsList>
              )}
            </SearchWrapper>
          </SearchWrapperContainer>
        )}
        
        {/* Bouton de recherche - visible uniquement quand la sidebar est fermée en mode mobile */}
        {(sidebarCollapsed || !isMobile) && (
          <ToggleButton onClick={toggleSearchVisibility}>
            {isSearchVisible ? <FaTimes /> : <FaSearch />}
          </ToggleButton>
        )}
        
        {/* Bouton d'ajout de catégorie supprimé - maintenant dans la sidebar */}
      </SearchContainer>
      {selectedLocation && (
        <UnifiedMarkerModal
          mode="create"
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveMarker}
          markerAddress={selectedLocation.address}
        />
      )}
    </>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;

